/**
 * Dark Mode Hook
 * Manages dark mode state with URL parameter override (for iframe embedding)
 * and localStorage persistence for standalone mode
 */

import { useEffect, useState, useCallback } from 'react';

export const DARK_MODE_STORAGE_KEY = 'darkMode';

/**
 * Clear any persisted dark-mode preference. Called on logout so the next user
 * on a shared browser starts from their own system default.
 */
export function clearDarkModePreference(): void {
  try { localStorage.removeItem(DARK_MODE_STORAGE_KEY); } catch { /* ignore */ }
}

// Check if we're embedded in an iframe
const isEmbedded = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true; // Cross-origin iframe
  }
};

// Get theme from URL parameter
const getThemeFromUrl = (): 'light' | 'dark' | null => {
  const params = new URLSearchParams(window.location.search);
  const theme = params.get('theme');
  if (theme === 'light' || theme === 'dark') {
    return theme;
  }
  return null;
};

export function useDarkMode() {
  const [isControlledByParent, setIsControlledByParent] = useState(false);

  const [isDark, setIsDark] = useState(() => {
    // Check URL parameter first (when embedded in iframe from dashboard)
    const urlTheme = getThemeFromUrl();
    if (urlTheme !== null) {
      return urlTheme === 'dark';
    }

    // Check localStorage second
    const stored = localStorage.getItem(DARK_MODE_STORAGE_KEY);
    if (stored !== null) {
      return stored === 'true';
    }

    // Default to system preference
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });

  // On mount, check if theme is controlled by parent (URL parameter present)
  useEffect(() => {
    const urlTheme = getThemeFromUrl();
    if (urlTheme !== null && isEmbedded()) {
      setIsControlledByParent(true);
    }
  }, []);

  // Listen for URL changes (in case parent updates the iframe URL)
  useEffect(() => {
    const handleUrlChange = () => {
      const urlTheme = getThemeFromUrl();
      if (urlTheme !== null) {
        setIsDark(urlTheme === 'dark');
        setIsControlledByParent(isEmbedded());
      }
    };

    // Listen for popstate (back/forward navigation)
    window.addEventListener('popstate', handleUrlChange);

    return () => {
      window.removeEventListener('popstate', handleUrlChange);
    };
  }, []);

  // Apply theme to document
  useEffect(() => {
    const root = document.documentElement;
    if (isDark) {
      root.classList.add('dark');
    } else {
      root.classList.remove('dark');
    }

    // Only save to localStorage if not controlled by parent
    if (!isControlledByParent) {
      localStorage.setItem(DARK_MODE_STORAGE_KEY, String(isDark));
    }
  }, [isDark, isControlledByParent]);

  const toggleDarkMode = useCallback(() => {
    // Don't allow toggle if controlled by parent iframe
    if (isControlledByParent) {
      return;
    }
    setIsDark(prev => !prev);
  }, [isControlledByParent]);

  return { isDark, toggleDarkMode, isControlledByParent };
}
